import { Close, Share } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Button, type ButtonType } from '~/components/Button';
import { CopyInputButton } from '~/components/CopyInputButton';
import { SocialShareButton } from '~/components/SocialShareButton';
import { cleanReportName } from '~/scripts/cleanReportName';

type ShareReportButtonProps = Omit<ButtonType, 'onClick'> & {
  uuid: string;
  reportTitle: string;
};

export default function ShareReportButton({
  uuid,
  reportTitle,
  ...buttonProps
}: ShareReportButtonProps) {
  const [open, setOpen] = useState(false);
  const shareUrl = `${import.meta.env.VITE_ORIGIN}/reports/${cleanReportName(reportTitle)}/${uuid}?utm_source=share-report`;
  const badgeBaseUrl = `https://badges.nerdydata.com/${uuid}`;
  const markdownBadge = `[![NerdyData.com Badge](${badgeBaseUrl})](${badgeBaseUrl}/report)`;
  const htmlBadge = `<a href="${shareUrl}"><img src="${badgeBaseUrl}" alt="NerdyData.com Badge" /></a>`;

  return (
    <Button
      variant="outlined"
      color="success"
      data-fullstory="share report"
      startIcon={<Share />}
      onClick={() => setOpen(true)}
      {...buttonProps}>
      Share Report
      {/* Dialog uses MUI Portal — renders at document.body in the DOM, not inside the button element */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Share Report</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={(event) => {
            event.stopPropagation();
            setOpen(false);
          }}
          sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>

        <DialogContent>
          <Stack spacing={3} divider={<Divider />}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Copy URL
              </Typography>
              <CopyInputButton value={shareUrl} fullWidth size="small" />
            </Box>

            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}>
                <Typography variant="subtitle2">
                  Embed NerdyData Badge
                </Typography>
                <img
                  src={badgeBaseUrl}
                  alt="NerdyData Badge preview"
                  style={{ maxHeight: 24 }}
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Markdown
                </Typography>
                <CopyInputButton value={markdownBadge} fullWidth size="small" />
                <Typography variant="caption" color="text.secondary">
                  HTML
                </Typography>
                <CopyInputButton value={htmlBadge} fullWidth size="small" />
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Share on Social
              </Typography>
              <Stack direction="row" spacing={1}>
                <SocialShareButton
                  social="twitter"
                  url={shareUrl}
                  title={reportTitle}
                  text="Share on X"
                />
                <SocialShareButton
                  social="linkedin"
                  url={shareUrl}
                  title={reportTitle}
                  text="Share on LinkedIn"
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </Button>
  );
}
